"use client";

/**
 * Streamed broad category view (Approach A, Phase 4, 2026-08-17).
 *
 * The live-building twin of FastResults. FastResults renders a broad category's
 * picks that the SSR already verified (one blocking ~36s resolveBroad); this
 * opens openBroadStream and builds the SAME page as the events arrive:
 *   - the ranked candidates land together at ~6s and animate in one-by-one
 *     (staggered entrance), each wearing a "Checking stock" chip;
 *   - each candidate's in-stock result streams in and flips its chip off
 *     (in stock) or fades the card out (out of stock, backfilled from the
 *     buffer so the list stays full);
 *   - `done` settles the confirmed, ranked set.
 *
 * DESIGN: byte-for-byte the FastResults surface - the same .cmp-surface main,
 * SearchAgainBar, header, `space-y-3` list, LIST_CARD_CLASS wrapper,
 * ListCardContent row (UNCHANGED - the chip is an absolute sibling, never an
 * edit to the card), footnote and Disclosures. A tap hands off to the ONE shared
 * /compare search exactly as FastResults does.
 *
 * Non-broad (exact/ask/out_of_scope) and stream failures are surfaced to the
 * parent via onNonBroad / onError so Phase 5 can fall through to today's flow;
 * this component owns only the broad, streamed list.
 */

import type { Route } from "next";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useReducer, useRef, useState } from "react";

import type { BroadProduct } from "@/lib/api/broad";
import { compareFromOffers } from "@/lib/api/broad";
import {
  openBroadStream,
  type BroadStreamEvent,
  type BroadStreamMode,
} from "@/lib/api/broad-stream";
import {
  BROAD_PRICE_FOOTNOTE,
  BROAD_SUBTITLE,
  cleanTitleForSearch,
} from "@/lib/broad-format";

import {
  BROAD_OFFERS_COMPARE_ENABLED,
  BROAD_STREAM_COMPARE_ENABLED,
  SEARCH_SCENE_ENABLED,
} from "@/lib/flags";

import { ComparisonShell } from "./ComparisonShell";
import { Disclosures } from "./Disclosures";
import { LIST_CARD_CLASS, ListCardContent } from "./ListCardContent";
import { SearchAgainBar } from "./SearchAgainBar";
import { SearchingSceneOverlay } from "./SearchingSceneOverlay";

/** Products shown. Mirrors the backend STREAM_LIMIT so the buffer candidates
 *  (verified but beyond the top 15) backfill out-of-stock drops without ever
 *  spilling past the target count. */
const SHOWN_LIMIT = 15;
/** Skeleton rows during the ~6s before the picks land (same footprint as a real
 *  card, so no layout shift when the real rows displace them). */
const SKELETON_ROWS = 6;

/** "office desk" -> "Best office desks". Strips a leading filler word first so a
 *  query the user typed as "best gaming keyboards" doesn't become "Best Best ...". */
function toHeading(query: string): string {
  const q = query.trim().replace(/^(best|top|cheapest|cheap|good|great)\s+/i, "");
  if (!q) return "Best options";
  return `Best ${q.endsWith("s") ? q : `${q}s`}`;
}

interface Verdict {
  in_stock: boolean;
  shop_count: number;
}

interface State {
  // resolving = mode not yet known (neutral screen, never the category header);
  // loading = confirmed broad, waiting for picks (header + skeletons);
  // list = picks in, streaming verification.
  status: "resolving" | "loading" | "list" | "nonbroad" | "error";
  slug: string | null;
  products: BroadProduct[];
  verified: Record<number, Verdict>;
  keptIndices: number[] | null;
  /** the mode event when it is NOT broad, so the parent can render the right screen */
  nonBroad: {
    mode: BroadStreamMode;
    heading: string | null;
    slug: string | null;
    product_label: string | null;
    reason: string | null;
  } | null;
}

const INITIAL: State = {
  status: "resolving",
  slug: null,
  products: [],
  verified: {},
  keptIndices: null,
  nonBroad: null,
};

function reducer(state: State, ev: BroadStreamEvent): State {
  switch (ev.kind) {
    case "keepalive":
      return state;
    case "mode":
      if (ev.mode === "broad") {
        // Now we know it IS a category: switch from the neutral resolving screen
        // to the header + skeletons until the picks land.
        return { ...state, status: "loading", slug: ev.slug };
      }
      return {
        ...state,
        status: "nonbroad",
        nonBroad: {
          mode: ev.mode,
          heading: ev.heading,
          slug: ev.slug,
          product_label: ev.product_label,
          reason: ev.reason,
        },
      };
    case "picks":
      return {
        ...state,
        status: "list",
        slug: ev.slug ?? state.slug,
        products: ev.products,
      };
    case "verified":
      return {
        ...state,
        verified: {
          ...state.verified,
          [ev.index]: { in_stock: ev.in_stock, shop_count: ev.shop_count },
        },
      };
    case "done":
      return { ...state, keptIndices: ev.kept_indices };
    case "error":
      // A hard stream failure (or a 404 when the flag is off) -> let the parent
      // fall back to the blocking resolveBroad. Never a dead-end here.
      return state.status === "list" ? state : { ...state, status: "error" };
    default:
      return state;
  }
}

interface Props {
  query: string;
  postcode: string;
  countryCode?: string;
  userId?: string | null;
  /** Fired when the stream classifies the query as NOT a broad category, so the
   *  parent renders today's exact / ask / out-of-scope screen. */
  onNonBroad?: (payload: NonNullable<State["nonBroad"]>) => void;
  /** Fired when the stream cannot start or fails before any picks arrive, so the
   *  parent falls back to the blocking resolveBroad path. */
  onError?: () => void;
  /** Fired once when the live build finishes (the `done` event), with the slug the
   *  backend saved the fresh picks under (_broad_slug(query), i.e. the /c/list key).
   *  Lets a parent that mounts this OFF /compare (e.g. Live Prices on /c/list) reveal
   *  the freshly-built category. Optional: /compare callers ignore it. */
  onComplete?: (slug: string | null) => void;
  /** The caller already KNOWS this query is a category (e.g. rebuilding an
   *  existing /c/list page): skip the full-screen scene during the "resolving"
   *  phase and show the in-flow header + skeleton surface immediately, so the
   *  build never takes over the chrome (the B-141 flash). /compare's fresh
   *  searches omit it and keep today's scene continuity. */
  assumeBroad?: boolean;
}

export function BroadStreamView({
  query,
  postcode,
  countryCode = "AU",
  userId = null,
  onNonBroad,
  onError,
  onComplete,
  assumeBroad = false,
}: Props) {
  const router = useRouter();
  const reduceMotion = useReducedMotion();
  const [state, dispatch] = useReducer(reducer, INITIAL);
  const [picked, setPicked] = useState(false);
  // Fresh live build -> "just now" for the Disclosures footer (computed once,
  // client-only, so no SSR hydration mismatch).
  const [fetchedAt] = useState(() => new Date().toISOString());
  // Indices confirmed out-of-stock that have shown their red chip and should now
  // fade out (removed from `visible`). Scheduled once per index below.
  const [dismissed, setDismissed] = useState<Set<number>>(() => new Set());
  const scheduledDismiss = useRef<Set<number>>(new Set());
  // Fire onComplete exactly once when the `done` event lands (ref-guarded so an
  // effect re-run cannot double-fire it).
  const completeFired = useRef(false);

  // Drive the stream. One AbortController tears the fetch (and its server-side
  // cluster fetches) down on unmount or when the query changes.
  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    (async () => {
      try {
        for await (const ev of openBroadStream(
          query,
          countryCode,
          controller.signal,
          userId,
        )) {
          if (cancelled) return;
          dispatch(ev);
        }
      } catch {
        // Fetch threw (non-2xx / 404-flag-off / network). Surface as an error so
        // the parent can fall back; guarded to only matter pre-picks.
        if (!cancelled) dispatch({ kind: "error", error: "stream_failed" });
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [query, countryCode, userId]);

  // Hand non-broad / error outcomes to the parent (Phase 5 wires the fallthrough).
  useEffect(() => {
    if (state.status === "nonbroad" && state.nonBroad) onNonBroad?.(state.nonBroad);
    if (state.status === "error") onError?.();
    // The live build finished (the `done` event set keptIndices): hand the saved
    // slug back so a parent that mounted this on /c/list can reveal the fresh page.
    if (state.keptIndices !== null && !completeFired.current) {
      completeFired.current = true;
      onComplete?.(state.slug);
    }
  }, [
    state.status,
    state.nonBroad,
    state.keptIndices,
    state.slug,
    onNonBroad,
    onError,
    onComplete,
  ]);

  // A candidate confirmed OUT OF STOCK shows its red chip for a beat, then is
  // dismissed (removed from `visible` -> framer-motion fades it out and the next
  // buffer candidate backfills). Scheduled once per index (ref-guarded so an
  // effect re-run never double-schedules).
  useEffect(() => {
    for (const [key, v] of Object.entries(state.verified)) {
      const idx = Number(key);
      if (v.in_stock === false && !scheduledDismiss.current.has(idx)) {
        scheduledDismiss.current.add(idx);
        window.setTimeout(() => {
          setDismissed((prev) => new Set(prev).add(idx));
        }, 900);
      }
    }
  }, [state.verified]);

  // Tap -> that product's comparison. Order: (1) an already-built /c page;
  // (2) GUARANTEED STREAMING tap (2026-08-19) - open /compare SEEDED with this
  // pick's known shops so the real final prices stream in and it can't dead-end;
  // (3) the legacy listed-only offers tap (Phase 2); (4) the live /compare search.
  // The loader (SearchingSceneOverlay) is already up via setPicked before we
  // navigate, so the tap never looks frozen; ComparisonStream then shows its own.
  const compare = async (p: BroadProduct) => {
    setPicked(true);
    if (p.comparison_slug) {
      router.push(`/c/${p.comparison_slug}` as Route);
      return;
    }
    const q = cleanTitleForSearch(p.title);
    const pc = encodeURIComponent(postcode);
    if (BROAD_STREAM_COMPARE_ENABLED && state.slug) {
      router.push(
        `/compare?q=${encodeURIComponent(q)}&postcode=${pc}&from_chip=1` +
          `&seed_list=${encodeURIComponent(state.slug)}` +
          `&seed_title=${encodeURIComponent(p.title)}` as Route,
      );
      return;
    }
    if (BROAD_OFFERS_COMPARE_ENABLED && state.slug) {
      const res = await compareFromOffers(state.slug, p.title, countryCode);
      if (res?.slug) {
        router.push(`/c/${res.slug}` as Route);
        return;
      }
    }
    router.push(
      `/compare?q=${encodeURIComponent(q)}&postcode=${pc}&from_chip=1` as Route,
    );
  };

  // The visible list. Before `done`: the first SHOWN_LIMIT candidates that are
  // not CONFIRMED out-of-stock (so a rare drop backfills from the buffer, and
  // still-checking ones keep their slot). After `done`: the authoritative kept
  // set, in its final ranked order.
  const visible = useMemo(() => {
    if (state.keptIndices) {
      return state.keptIndices
        .map((i) => ({ p: state.products[i], i }))
        .filter((x): x is { p: BroadProduct; i: number } => Boolean(x.p));
    }
    return state.products
      .map((p, i) => ({ p, i }))
      // Keep an out-of-stock card visible (showing its red chip) until its
      // dismiss timer fires; only THEN does it leave and the buffer backfill.
      .filter(({ i }) => !dismissed.has(i))
      .slice(0, SHOWN_LIMIT);
  }, [state.products, state.keptIndices, dismissed]);

  // A committed tap shows the same search transition FastResults uses.
  if (picked) {
    return <SearchingSceneOverlay loaded={0} failed={0} total={0} isDone={false} />;
  }

  // Non-broad / error: the parent has been notified (effect above) and swaps in
  // today's flow. On /compare (!assumeBroad) render the SAME continuation scene
  // during the swap - a null here was the 2026-08-28 blank-screen bug: nothing
  // painted while the parent's next surface mounted. assumeBroad callers
  // (/c/list rebuild) keep null: their reveal() shows the page's existing
  // content in the same render cycle, and a full-screen scene would cover it.
  if (state.status === "nonbroad" || state.status === "error") {
    if (assumeBroad) return null;
    return SEARCH_SCENE_ENABLED ? (
      <SearchingSceneOverlay loaded={0} failed={0} total={0} isDone={false} />
    ) : (
      <ComparisonShell />
    );
  }

  // Until the stream confirms it IS a category (mode=broad), show the SAME
  // loading surface /compare's loading.tsx shows during its server phase, so the
  // shopper sees ONE continuous scene from submit to result instead of that
  // scene switching to a different spinner. It is also neutral (no category
  // header), so an exact/out-of-scope query never flashes a wrong "Best <query>s"
  // heading before it falls through.
  if (state.status === "resolving" && !assumeBroad) {
    return SEARCH_SCENE_ENABLED ? (
      <SearchingSceneOverlay loaded={0} failed={0} total={0} isDone={false} />
    ) : (
      <ComparisonShell />
    );
  }
  // assumeBroad + resolving falls through to the main surface below with the
  // skeleton branch, so the caller-known category shows header + skeletons from
  // the first frame (no full-screen scene, chrome untouched).

  const item = reduceMotion
    ? { hidden: { opacity: 0 }, show: { opacity: 1 } }
    : { hidden: { opacity: 0, y: 8 }, show: { opacity: 1, y: 0 } };
  const container = {
    hidden: {},
    show: { transition: { staggerChildren: reduceMotion ? 0 : 0.05 } },
  };

  return (
    <main className="cmp-surface mx-auto min-h-screen max-w-3xl bg-background px-6 pb-16">
      <SearchAgainBar initialQuery={query} />

      <header className="mt-6">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
          {toHeading(query)}
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          {BROAD_SUBTITLE}
        </p>
      </header>

      {state.status === "loading" || state.status === "resolving" ? (
        <section className="mt-6 space-y-3" aria-hidden="true">
          {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
            <SkeletonRow key={i} />
          ))}
        </section>
      ) : (
        <motion.section
          className="mt-6 flex flex-col gap-3"
          variants={container}
          initial="hidden"
          animate="show"
        >
          <AnimatePresence mode="popLayout" initial={false}>
            {visible.map(({ p, i }) => {
              const st = stockState(state.verified[i], Boolean(state.keptIndices));
              return (
                <motion.button
                  key={p.title + i}
                  type="button"
                  layout
                  variants={item}
                  exit={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.97 }}
                  transition={{ duration: 0.25 }}
                  onClick={() => compare(p)}
                  className={LIST_CARD_CLASS}
                  aria-busy={st === "checking"}
                >
                  <ListCardContent product={p} statusChip={<StockChip state={st} />} />
                </motion.button>
              );
            })}
          </AnimatePresence>
        </motion.section>
      )}

      <p className="mt-5 text-center text-sm text-muted-foreground">
        {BROAD_PRICE_FOOTNOTE}
      </p>

      <Disclosures
        postcode={postcode}
        fetchedAt={fetchedAt}
        cacheHit={false}
        isStale={false}
        priceBasis="listed"
      />
    </main>
  );
}

type StockState = "checking" | "instock" | "oos";

/** The card's availability chip state. After `done`, everything shown is verified
 *  in stock; before that, it is the streamed per-card verdict. */
function stockState(v: Verdict | undefined, done: boolean): StockState {
  if (done) return "instock";
  if (v === undefined) return "checking";
  return v.in_stock ? "instock" : "oos";
}

/** Availability chip, rendered INSIDE the card body below the rating (the approved
 *  Show-Then-Verify placement, never over the price). Flips checking (amber,
 *  pulsing) -> in stock (green); or -> out of stock (red) for the rare miss just
 *  before the card fades out. Colours follow the codebase's semantic ramp
 *  (IBoughtThisButton), so they read in both light and dark. */
function StockChip({ state }: { state: StockState }) {
  const tone =
    state === "instock"
      ? "bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300"
      : state === "oos"
        ? "bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-300"
        : "bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300";
  const label =
    state === "instock" ? "In stock" : state === "oos" ? "Out of stock" : "Checking stock";
  return (
    <span
      className={`mt-2 inline-flex w-max items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone}`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full bg-current ${
          state === "checking" ? "animate-pulse" : ""
        }`}
      />
      {label}
    </span>
  );
}

/** Loading placeholder on the REAL card wrapper (LIST_CARD_CLASS), so its
 *  footprint matches a real row exactly and nothing shifts when picks land. */
function SkeletonRow() {
  const bar = "rounded bg-muted-foreground/15 animate-pulse";
  return (
    <div className={LIST_CARD_CLASS} aria-hidden="true">
      <div className="flex items-start gap-3 sm:contents">
        <div className="h-20 w-20 shrink-0 rounded-lg bg-muted-foreground/15 animate-pulse sm:h-24 sm:w-24" />
        <div className="flex min-w-0 flex-1 flex-col justify-center gap-2">
          <div className={`${bar} h-2.5 w-16`} />
          <div className={`${bar} h-4 w-4/5`} />
          <div className={`${bar} h-3 w-1/2`} />
        </div>
      </div>
      <div className="mt-3 flex shrink-0 items-center justify-between gap-2 border-t border-border/60 pt-3 sm:mt-0 sm:flex-col sm:items-end sm:justify-center sm:gap-1.5 sm:border-0 sm:pl-2 sm:pt-0">
        <div className={`${bar} h-4 w-16`} />
        <div className={`${bar} h-3 w-20`} />
      </div>
    </div>
  );
}
